import { AfterViewInit, Component, computed, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { NgxOtpInputComponent, NgxOtpInputComponentOptions, NgxOtpStatus } from "ngx-otp-input";
import { AuthService } from "../../../../../core/auth/auth.service";
import { SupabaseService } from "../../../../../core/supabase.service";
import { NotificationsService } from "../../../../../services/notifications.service";
import { BtnComponent } from "../../../../../components/ui/buttons/btn/btn.component";
import { AvatarComponent } from "../../../../../components/ui/avatar/avatar.component";
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { iconoirMail, iconoirLock, iconoirArrowRight, iconoirKey, iconoirUser, iconoirWarningCircle } from '@ng-icons/iconoir';

interface TestAccount {
  name: string
  email: string
  password: string
  roles: string[]
  agency: string
  description: string
}

interface RoleGroup {
  role: string
  roleKey: string
  /** Organisation d'appartenance — pilote les onglets de la dialog */
  organisation: string
  accounts: TestAccount[]
}

import { LoginBySocialAuthComponent } from '../login-by-social-auth/login-by-social-auth.component';

@Component({
  selector: 'app-login-by-email',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    NgxOtpInputComponent,
    NgIconComponent,
    BtnComponent,
    AvatarComponent,
    LoginBySocialAuthComponent
  ],
  providers: [
    provideIcons({
      iconoirMail,
      iconoirLock,
      iconoirArrowRight,
      iconoirKey,
      iconoirUser,
      iconoirWarningCircle
    })
  ],
  templateUrl: './login-by-email.component.html',
  standalone: true,
  styleUrl: './login-by-email.component.scss'
})
export class LoginByEmailComponent implements AfterViewInit {

  public emailCtrl = new FormControl('', [Validators.required, Validators.email])
  public passwordCtrl = new FormControl('', [Validators.required])
  public emailSend = signal(false)
  public supabase = inject(SupabaseService)
  public auth = inject(AuthService)
  public notifications = inject(NotificationsService)
  public counter = signal(0)

  public isLocalhost = window.location.hostname == 'localhost'
  public connexionByPassword = signal(false)

  /**
   * Erreur de connexion affichée sous le formulaire (identifiants refusés,
   * envoi du code impossible) : ce qui s'est passé et quoi faire.
   */
  public readonly errorMessage = signal<string | null>(null)

  // Structure groupée par rôle pour la popover "Comptes de test" (dev/localhost uniquement)
  public readonly roleGroups: RoleGroup[] = [
    {
      role: 'Administrateur Mokah+',
      roleKey: 'WA_ADMIN',
      organisation: 'Web-atrio',
      accounts: [
        { name: 'Jean Dupont', email: 'admin@admin.com', password: 'admin', roles: ['WA_ADMIN', 'SUPER_ADMIN'], agency: 'Paris', description: 'Super admin avec tous les accès plateforme' }
      ]
    },
    {
      role: 'Plan Starter — 7 € HT/siège',
      roleKey: 'PLAN_STARTER',
      organisation: 'Starter Démo',
      accounts: [
        { name: 'Alice Starter', email: 'admin@starter-demo.com', password: 'password123', roles: ['SUPER_ADMIN'], agency: 'Starter Démo', description: 'Admin org plan Starter (Calendrier + Projets)' },
        { name: 'Claude Starter', email: 'calendar@starter-demo.com', password: 'password123', roles: ['CALENDAR_MANAGER'], agency: 'Starter Démo', description: 'Gestionnaire calendrier — plan Starter' },
        { name: 'Paul Starter', email: 'projects@starter-demo.com', password: 'password123', roles: ['PROJECTS'], agency: 'Starter Démo', description: 'Gestionnaire projets — plan Starter' },
        { name: 'Eva Starter', email: 'user@starter-demo.com', password: 'password123', roles: [], agency: 'Starter Démo', description: 'Employée sans rôle — plan Starter' }
      ]
    },
    {
      role: 'Plan Growth — 14 € HT/siège',
      roleKey: 'PLAN_GROWTH',
      organisation: 'Growth Démo',
      accounts: [
        { name: 'Bruno Growth', email: 'admin@growth-demo.com', password: 'password123', roles: ['SUPER_ADMIN'], agency: 'Growth Démo', description: 'Admin org plan Growth (modules cœur)' },
        { name: 'Rita Growth', email: 'rh@growth-demo.com', password: 'password123', roles: ['COMITE_SALARIES'], agency: 'Growth Démo', description: 'Gestionnaire RH — plan Growth' },
        { name: 'Ivan Growth', email: 'inventory@growth-demo.com', password: 'password123', roles: ['INVENTORIES'], agency: 'Growth Démo', description: 'Gestionnaire inventaires — plan Growth' },
        { name: 'Ugo Growth', email: 'user@growth-demo.com', password: 'password123', roles: [], agency: 'Growth Démo', description: 'Employé sans rôle — plan Growth' }
      ]
    },
    {
      role: 'Plan Scale — 18 € HT/siège',
      roleKey: 'PLAN_SCALE',
      organisation: 'Scale Démo',
      accounts: [
        { name: 'Sarah Lambert', email: 'admin@scale-demo.com', password: 'password123', roles: ['SUPER_ADMIN'], agency: 'Scale Démo', description: 'Admin org plan Scale (tout + IA/Voice)' },
        { name: 'Marc Dubreuil', email: 'manager@scale-demo.com', password: 'password123', roles: ['CALENDAR_MANAGER', 'PROJECTS'], agency: 'Scale Démo', description: 'Manager multi-rôles — plan Scale' },
        { name: 'Umberto Rossi', email: 'user@scale-demo.com', password: 'password123', roles: [], agency: 'Scale Démo', description: 'Employé sans rôle — plan Scale (teste IA/Voice)' }
      ]
    },
    {
      // Démo du module RH (MOKA-388) — seed 97_demo_rh_valmont.sql
      role: 'Direction et RH',
      roleKey: 'VALMONT_RH',
      organisation: 'Cabinet Valmont',
      accounts: [
        { name: 'Hélène Valmont', email: 'helene.valmont@valmont-demo.fr', password: 'password123', roles: ['SUPER_ADMIN'], agency: 'Bordeaux, Nantes, Toulouse', description: 'Associée gérante — admin de l’organisation, voit tout le module RH' },
        { name: 'Claire Fontaine', email: 'claire.fontaine@valmont-demo.fr', password: 'password123', roles: ['COMITE_SALARIES'], agency: 'Bordeaux, Nantes, Toulouse', description: 'Responsable RH — gestionnaire RH des trois agences' },
        { name: 'Laura Michel', email: 'laura.michel@valmont-demo.fr', password: 'password123', roles: ['COMITE_SALARIES'], agency: 'Nantes', description: 'Chargée RH — gestionnaire RH de l’agence de Nantes seulement' },
        { name: 'Karine Lopez', email: 'karine.lopez@valmont-demo.fr', password: 'password123', roles: ['COMITE_SALARIES'], agency: 'Bordeaux, Toulouse', description: 'Gestionnaire de paie — gestionnaire RH de Bordeaux et Toulouse' },
        { name: 'Marc Delmas', email: 'marc.delmas@valmont-demo.fr', password: 'password123', roles: ['CALENDAR_MANAGER'], agency: 'Toulouse', description: 'Associé de Toulouse — gestionnaire du calendrier, sans accès RH' }
      ]
    },
    {
      role: 'Collaborateurs',
      roleKey: 'VALMONT_SALARIES',
      organisation: 'Cabinet Valmont',
      accounts: [
        { name: 'Emma Lefort', email: 'emma.lefort@valmont-demo.fr', password: 'password123', roles: [], agency: 'Bordeaux', description: 'Collaboratrice comptable — entretien annuel et baromètre à remplir' },
        { name: 'Hugo Lambert', email: 'hugo.lambert@valmont-demo.fr', password: 'password123', roles: [], agency: 'Bordeaux', description: 'Auditeur junior arrivé il y a 4 mois — rapport d’étonnement à remplir' },
        { name: 'Antoine Leclerc', email: 'antoine.leclerc@valmont-demo.fr', password: 'password123', roles: [], agency: 'Bordeaux', description: 'Chef de mission — négociation en cours, objectifs de carrière' },
        { name: 'Samuel Weber', email: 'samuel.weber@valmont-demo.fr', password: 'password123', roles: [], agency: 'Bordeaux', description: 'Arrivé ce mois-ci — check-list d’arrivée en cours dans « Ma carrière »' }
      ]
    },
    {
      role: 'Gestionnaire RH',
      roleKey: 'COMITE_SALARIES',
      organisation: 'Web-atrio',
      accounts: [
        { name: 'Marie Dubois', email: 'user@example.com', password: 'password123', roles: ['COMITE_SALARIES'], agency: 'Paris', description: 'Gestionnaire RH' },
        { name: 'Sophie Bernard', email: 'user3@example.com', password: 'password123', roles: ['COMITE_SALARIES'], agency: 'Paris', description: 'Gestionnaire RH' },
        { name: 'Isabelle Chevalier', email: 'user31@example.com', password: 'password123', roles: ['COMITE_SALARIES'], agency: 'Lyon', description: 'Gestionnaire RH (Lyon)' }
      ]
    },
    {
      role: 'Calendrier',
      roleKey: 'CALENDAR_MANAGER',
      organisation: 'Web-atrio',
      accounts: [
        { name: 'Pierre Martin', email: 'user2@example.com', password: 'password123', roles: ['CALENDAR_MANAGER'], agency: 'Paris', description: 'Gestionnaire du calendrier de présences' },
        { name: 'Melanie Masson', email: 'user33@example.com', password: 'password123', roles: ['CALENDAR_MANAGER'], agency: 'Lyon', description: 'Gestionnaire du calendrier (Lyon)' }
      ]
    },
    {
      role: 'Projets',
      roleKey: 'PROJECTS',
      organisation: 'Web-atrio',
      accounts: [
        { name: 'Lucas Moreau', email: 'user4@example.com', password: 'password123', roles: ['PROJECTS'], agency: 'Paris', description: 'Gestionnaire de projets' },
        { name: 'Adrien Faure', email: 'user34@example.com', password: 'password123', roles: ['PROJECTS'], agency: 'Lyon', description: 'Gestionnaire de projets (Lyon)' }
      ]
    },
    {
      role: 'Inventaires',
      roleKey: 'INVENTORIES',
      organisation: 'Web-atrio',
      accounts: [
        { name: 'Thomas Petit', email: 'user6@example.com', password: 'password123', roles: ['INVENTORIES'], agency: 'Paris', description: 'Gestionnaire des inventaires' },
        { name: 'Cedric Jacquet', email: 'user42@example.com', password: 'password123', roles: ['INVENTORIES'], agency: 'Lyon', description: 'Gestionnaire des inventaires (Lyon)' }
      ]
    },
    {
      role: 'Sans rôle',
      roleKey: 'NONE',
      organisation: 'Web-atrio',
      accounts: [
        { name: 'Emma Leroy', email: 'user5@example.com', password: 'password123', roles: [], agency: 'Paris', description: 'Utilisateur standard sans rôle spécifique' }
      ]
    }
  ]

  // Accordéon : groupe ouvert par défaut = Super Admin
  public openGroup = signal<string | null>('WA_ADMIN')

  // Onglets par organisation (ordre d'apparition dans roleGroups)
  public readonly organisationTabs: string[] = [...new Set(this.roleGroups.map(g => g.organisation))]
  public activeOrgTab = signal<string>(this.organisationTabs[0])

  // Recherche dans les comptes de test
  public searchQuery = signal('')

  public filteredGroups = computed(() => {
    const query = this.searchQuery().toLowerCase().trim()
    // La recherche est GLOBALE (toutes organisations) ; sans recherche, on filtre par onglet.
    if (!query) return this.roleGroups.filter(g => g.organisation === this.activeOrgTab())
    return this.roleGroups
      .map(group => ({
        ...group,
        accounts: group.accounts.filter(a =>
          a.name.toLowerCase().includes(query) ||
          a.email.toLowerCase().includes(query) ||
          a.agency.toLowerCase().includes(query) ||
          a.roles.some(r => r.toLowerCase().includes(query)) ||
          group.role.toLowerCase().includes(query) ||
          group.organisation.toLowerCase().includes(query)
        )
      }))
      .filter(group => group.accounts.length > 0)
  })

  public toggleGroup(roleKey: string): void {
    this.openGroup.update(current => current === roleKey ? null : roleKey)
  }

  public selectOrgTab(organisation: string): void {
    this.activeOrgTab.set(organisation)
    // Ouvre le premier groupe de l'organisation sélectionnée
    const first = this.roleGroups.find(g => g.organisation === organisation)
    this.openGroup.set(first?.roleKey ?? null)
  }

  public showDevPanel = signal(false)
  public loggingIn = signal<string | null>(null)


  public otpOptions: NgxOtpInputComponentOptions = {
    otpLength: 6,
    autoFocus: true,
    autoBlur: true,
  }
  public otpStatut: NgxOtpStatus | null = null

  constructor() {
    // En localhost, on peut activer le mode mot de passe avec des valeurs pré-remplies
    if (this.isLocalhost) {
      this.emailCtrl.setValue('admin@admin.com')
    }
  }

  public interval: any

  public async sendLoginEmail() {
    if (this.counter() > 0) {
      clearInterval(this.interval)
      this.counter.set(0)
    }
    this.emailCtrl.markAsTouched()
    if (this.emailCtrl.valid) {
      this.errorMessage.set(null)
      try {
        this.counter.set(30)
        const { error } = await this.supabase.client.auth.signInWithOtp({
          email: this.emailCtrl.value as string,
          options: {
            shouldCreateUser: false,
            emailRedirectTo: window.location.protocol + "//" + window.location.host + "/sign-in",
          },
        })

        if (error) {
          this.errorMessage.set(this.otpSendErrorMessage(error.message, error.status))
          this.counter.set(0)
          return
        }

        this.emailSend.set(true)
        this.notifications.showSuccess('Code de connexion envoyé')
        this.interval = setInterval(() => {
          this.counter.update(c => c - 1)
          if (this.counter() === 0) {
            clearInterval(this.interval)
          }
        }, 1000)
      } catch (error: any) {
        this.counter.set(0)
        this.errorMessage.set('Le code n\'a pas pu être envoyé. Vérifiez votre connexion internet, puis réessayez.')
        console.error('Error sending login email:', error)
      }
    }
  }

  public async login() {
    if (this.emailCtrl.valid && this.passwordCtrl.valid) {
      this.errorMessage.set(null)
      try {
        const { error } = await this.supabase.client.auth.signInWithPassword({
          email: this.emailCtrl.value as string,
          password: this.passwordCtrl.value as string
        })
        
        if (error) {
          let errorMessage: string
          if (error.message.includes('Invalid login credentials')) {
            errorMessage = 'Email ou mot de passe incorrect. Vérifiez votre saisie, ou recevez plutôt un code de connexion par email.'
          } else if (error.message.includes('Email not confirmed')) {
            errorMessage = 'Votre adresse email n\'est pas encore confirmée. Ouvrez l\'email de bienvenue reçu à l\'inscription, puis réessayez.'
          } else if (error.status === 429 || error.message.toLowerCase().includes('too many requests')) {
            errorMessage = 'Trop de tentatives de connexion. Patientez quelques minutes avant de réessayer.'
          } else {
            errorMessage = `Connexion impossible (${error.message}). Réessayez dans un instant.`
          }
          this.errorMessage.set(errorMessage)
          return
        }
        
        this.notifications.showSuccess('Connexion réussie')
      } catch (error: any) {
        this.errorMessage.set('La connexion n\'a pas abouti. Vérifiez votre connexion internet, puis réessayez.')
        console.error('Error during login:', error)
      }
    } else {
      this.emailCtrl.markAsTouched()
      this.passwordCtrl.markAsTouched()
      this.notifications.showWarning('Renseignez votre adresse email et votre mot de passe pour vous connecter.')
    }
  }

  public async changeOtp(event: any) {
    let otp = event.join('')
    if (otp.length === 6) {
      try {
        const { error } = await this.supabase.client.auth.verifyOtp({
          email: this.emailCtrl.value as string,
          token: otp,
          type: 'email'
        })
        
        if (error) {
          let errorMessage = 'Ce code n\'est pas valide. Vérifiez les 6 chiffres reçus par email.'
          if (error.message.includes('expired')) {
            errorMessage = 'Ce code a expiré. Cliquez sur « Renvoyer le code » pour en recevoir un nouveau.'
          } else if (error.message.includes('invalid')) {
            errorMessage = 'Ce code est incorrect. Vérifiez les 6 chiffres reçus par email.'
          } else {
            errorMessage = `Vérification du code impossible (${error.message}). Réessayez dans un instant.`
          }
          this.notifications.showError(errorMessage)
          this.otpStatut = NgxOtpStatus.FAILED
          return
        }
        
        this.notifications.showSuccess('Connexion réussie')
        this.otpStatut = NgxOtpStatus.SUCCESS
      } catch (error: any) {
        console.log(error)

        this.notifications.showError('Le code n\'a pas pu être vérifié. Vérifiez votre connexion internet, puis réessayez.')
        this.otpStatut = NgxOtpStatus.FAILED
        console.error('Error during OTP verification:', error)
      }
    }
  }

  /** Message d'échec d'envoi du code : ce qui s'est passé et quoi faire. */
  private otpSendErrorMessage(message: string, status?: number): string {
    const lower = message.toLowerCase()
    // shouldCreateUser: false → Supabase refuse un email inconnu
    if (lower.includes('signups not allowed') || lower.includes('user not found')) {
      return 'Aucun compte n\'est associé à cette adresse. Vérifiez-la, ou créez votre espace depuis l\'onglet Inscription.'
    }
    if (status === 429 || lower.includes('rate limit') || lower.includes('security purposes')) {
      return 'Un code vient déjà de vous être envoyé. Patientez une minute avant d\'en demander un nouveau.'
    }
    return `Le code n'a pas pu être envoyé (${message}). Vérifiez l'adresse, puis réessayez.`
  }

  public switchToPasswordMode() {
    this.connexionByPassword.set(true)
    this.emailSend.set(false)
    this.passwordCtrl.setValue('')
  }

  public switchToEmailMode() {
    this.connexionByPassword.set(false)
    this.emailSend.set(false)
    this.passwordCtrl.setValue('')
    this.otpStatut = null
  }

  public async quickLogin(account: TestAccount) {
    if (this.loggingIn()) return
    this.loggingIn.set(account.email)

    try {
      const { error } = await this.supabase.client.auth.signInWithPassword({
        email: account.email,
        password: account.password
      })

      if (error) {
        this.notifications.showError(`Connexion au compte de test impossible (${error.message}). La base locale est-elle bien seedée ?`)
        this.loggingIn.set(null)
        return
      }

      this.notifications.showSuccess(`Connexion en tant que ${account.name}`)
    } catch (error: any) {
      this.notifications.showError('Une erreur inattendue est survenue lors de la connexion')
      this.loggingIn.set(null)
      console.error('Error during quick login:', error)
    }
  }

  ngAfterViewInit() {

  }

}
